import { api } from './api'

export interface Notification {
  id: number
  organization_id: number
  kind: string | null
  notification: string | null
  origin: string | null
  url: string | null
  created_at: string | null
  updated_at: string | null
}

export interface NotificationsResponse {
  notifications: Notification[]
  total: number
  limit: number
  offset: number
  has_more: boolean
}

export interface HandsUpResolution {
  status: 'approved' | 'rejected' | 'modified'
  resolution_notes?: string
  modified_action?: Record<string, unknown>
}

// Notification kinds used by agents
export const AGENT_NOTIFICATION_KINDS = {
  RECOMMENDATION: 'agent_recommendation',
  HANDS_UP: 'agent_hands_up',
  HANDS_UP_CRITICAL: 'agent_hands_up_critical',
  HANDS_UP_APPROVED: 'agent_hands_up_approved',
  HANDS_UP_REJECTED: 'agent_hands_up_rejected',
  INSIGHT: 'agent_insight',
  REPORT: 'agent_report',  // Consolidated execution report
} as const

// Notification kinds from production (control systems, optimizations)
export const SYSTEM_NOTIFICATION_KINDS = {
  CONTROL_SYSTEMS: 'control-systems',
  OPTIMIZATION_RECOMMENDATION: 'optimization_recommendation',
} as const

// Check if a notification is from control systems
export function isControlSystemsNotification(kind: string | null): boolean {
  if (!kind) return false
  return kind === SYSTEM_NOTIFICATION_KINDS.CONTROL_SYSTEMS
}

// Check if a notification is an optimization recommendation
export function isOptimizationNotification(kind: string | null): boolean {
  if (!kind) return false
  return kind === SYSTEM_NOTIFICATION_KINDS.OPTIMIZATION_RECOMMENDATION
}

// Check if a notification is from an agent
export function isAgentNotification(kind: string | null): boolean {
  if (!kind) return false
  return kind.startsWith('agent_')
}

// Check if a notification requires action (hands-up) - pending resolution
export function isHandsUpNotification(kind: string | null): boolean {
  if (!kind) return false
  return kind === AGENT_NOTIFICATION_KINDS.HANDS_UP || kind === AGENT_NOTIFICATION_KINDS.HANDS_UP_CRITICAL
}

// Check if a hands-up notification has been resolved (approved or rejected)
export function isResolvedHandsUpNotification(kind: string | null): boolean {
  if (!kind) return false
  return kind === AGENT_NOTIFICATION_KINDS.HANDS_UP_APPROVED || kind === AGENT_NOTIFICATION_KINDS.HANDS_UP_REJECTED
}

// Check if a hands-up was approved
export function isApprovedHandsUpNotification(kind: string | null): boolean {
  if (!kind) return false
  return kind === AGENT_NOTIFICATION_KINDS.HANDS_UP_APPROVED
}

// Extract resolution info from notification origin
// Format: *_resolved_by_{email}_at_{timestamp}
export function extractResolutionInfo(origin: string | null): { resolvedBy: string; resolvedAt: string } | null {
  if (!origin) return null
  const match = origin.match(/_resolved_by_(.+)_at_(\d{4}-\d{2}-\d{2}T[\d:.]+Z?)$/)
  if (!match) return null
  return {
    resolvedBy: match[1],
    resolvedAt: match[2],
  }
}

// Check if a notification is an agent report
export function isAgentReportNotification(kind: string | null): boolean {
  if (!kind) return false
  return kind === AGENT_NOTIFICATION_KINDS.REPORT
}

// Get severity color for notification kind
export function getNotificationKindColor(kind: string | null): string {
  switch (kind) {
    case AGENT_NOTIFICATION_KINDS.HANDS_UP_CRITICAL:
      return 'bg-red-100 text-red-800 border-red-200'
    case AGENT_NOTIFICATION_KINDS.HANDS_UP:
      return 'bg-orange-100 text-orange-800 border-orange-200'
    case AGENT_NOTIFICATION_KINDS.HANDS_UP_APPROVED:
      return 'bg-green-100 text-green-800 border-green-200'
    case AGENT_NOTIFICATION_KINDS.HANDS_UP_REJECTED:
      return 'bg-red-100 text-red-800 border-red-200'
    case AGENT_NOTIFICATION_KINDS.RECOMMENDATION:
      return 'bg-blue-100 text-blue-800 border-blue-200'
    case AGENT_NOTIFICATION_KINDS.INSIGHT:
      return 'bg-gray-100 text-gray-800 border-gray-200'
    case AGENT_NOTIFICATION_KINDS.REPORT:
      return 'bg-indigo-100 text-indigo-800 border-indigo-200'
    case SYSTEM_NOTIFICATION_KINDS.CONTROL_SYSTEMS:
      return 'bg-purple-100 text-purple-800 border-purple-200'
    case SYSTEM_NOTIFICATION_KINDS.OPTIMIZATION_RECOMMENDATION:
      return 'bg-green-100 text-green-800 border-green-200'
    default:
      return 'bg-slate-100 text-slate-800 border-slate-200'
  }
}

// Get display label for notification kind
export function getNotificationKindLabel(kind: string | null): string {
  switch (kind) {
    case AGENT_NOTIFICATION_KINDS.HANDS_UP_CRITICAL:
      return 'Critical'
    case AGENT_NOTIFICATION_KINDS.HANDS_UP:
      return 'Action Required'
    case AGENT_NOTIFICATION_KINDS.HANDS_UP_APPROVED:
      return 'Approved'
    case AGENT_NOTIFICATION_KINDS.HANDS_UP_REJECTED:
      return 'Rejected'
    case AGENT_NOTIFICATION_KINDS.RECOMMENDATION:
      return 'Recommendation'
    case AGENT_NOTIFICATION_KINDS.INSIGHT:
      return 'Insight'
    case AGENT_NOTIFICATION_KINDS.REPORT:
      return 'System Analysis'
    case SYSTEM_NOTIFICATION_KINDS.CONTROL_SYSTEMS:
      return 'Control Systems'
    case SYSTEM_NOTIFICATION_KINDS.OPTIMIZATION_RECOMMENDATION:
      return 'Optimization'
    default:
      return kind || 'Notification'
  }
}

// Extract hands-up ID from notification origin
export function extractHandsUpId(origin: string | null): number | null {
  if (!origin) return null
  const match = origin.match(/handsup_(\d+)/)
  return match ? parseInt(match[1], 10) : null
}

// Extract agent ID from notification origin
export function extractAgentId(origin: string | null): number | null {
  if (!origin) return null
  const match = origin.match(/agent_(\d+)/)
  return match ? parseInt(match[1], 10) : null
}

export const notificationsService = {
  async getNotifications(
    organizationId: number,
    options: { limit?: number; offset?: number } = {}
  ): Promise<NotificationsResponse> {
    const { limit = 50, offset = 0 } = options
    const response = await api.get(`/notifications/${organizationId}`, {
      params: { limit, offset }
    })

    // Handle both old format (array) and new format (paginated object)
    if (Array.isArray(response.data)) {
      // Old format: API returns array directly
      const notifications = response.data as Notification[]
      return {
        notifications,
        total: notifications.length,
        limit,
        offset,
        has_more: false
      }
    }

    // New format: API returns paginated response
    return response.data
  },

  // Get pending recommendations count per agent
  async getPendingRecommendationCounts(organizationId: number): Promise<Record<number, number>> {
    // Fetch all notifications to count (using high limit)
    const response = await this.getNotifications(organizationId, { limit: 100 })
    const counts: Record<number, number> = {}

    response.notifications.forEach(n => {
      if (n.kind === AGENT_NOTIFICATION_KINDS.RECOMMENDATION && n.origin) {
        const match = n.origin.match(/agent_(\d+)/)
        if (match) {
          const agentId = parseInt(match[1], 10)
          counts[agentId] = (counts[agentId] || 0) + 1
        }
      }
    })

    return counts
  },

  // Mark a recommendation as done (delete the notification)
  async markRecommendationAsDone(notificationId: number): Promise<void> {
    await api.delete(`/notifications/${notificationId}`)
  },

  // Dismiss a recommendation (delete the notification)
  async dismissRecommendation(notificationId: number): Promise<void> {
    await api.delete(`/notifications/${notificationId}`)
  },

  // Resolve a hands-up notification (approve, reject, or modify)
  async resolveHandsUp(
    _agentId: number,
    handsUpId: number,
    resolution: HandsUpResolution,
    resolvedBy: string
  ): Promise<void> {
    await api.post(
      `/hands-up/${handsUpId}/resolve`,
      resolution,
      { params: { resolved_by: resolvedBy } }
    )
  },

  // Get hands-up details
  async getHandsUp(_agentId: number, handsUpId: number): Promise<Record<string, unknown>> {
    const response = await api.get(`/hands-up/${handsUpId}`)
    return response.data
  },
}
